// React
import { useCallback } from "react";

// Components
import { Input, Select } from "@/shared/ui/components";

// Hooks
import { useTranslation } from "@/shared/hooks";

// Types
import type { PartnerAccountType } from "@/shared/api/verificationApi";

/**
 * Step 1 form data
 */
export interface StepOneData {
  accountType: PartnerAccountType;
  companyName: string;
  licenseNumber: string;
  corporateEmail: string;
  officialWebsite: string;
}

/**
 * Props for VerificationStepOne
 */
interface VerificationStepOneProps {
  data: StepOneData;
  onChange: (field: keyof StepOneData, value: string) => void;
  errors: Partial<Record<keyof StepOneData, string>>;
}

/**
 * VerificationStepOne Component
 *
 * Registration form for verification (Step 1):
 * - Account Type (Corporate/Individual)
 * - Company Name
 * - License Number with Japanese format
 * - Corporate Email (no free providers)
 * - Official Website
 */
export function VerificationStepOne({
  data,
  onChange,
  errors,
}: VerificationStepOneProps) {
  const t = useTranslation();

  const handleChange = useCallback(
    (field: keyof StepOneData) =>
      (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        onChange(field, e.target.value);
      },
    [onChange],
  );

  return (
    <div className="space-y-4">
      {/* Account Type */}
      <div>
        <Select
          label={t("verificationAccountType")}
          value={data.accountType}
          onChange={(val) => onChange("accountType", String(val))}
          options={[
            {
              value: "corporate",
              label: t("verificationAccountTypeCorporate"),
            },
            {
              value: "individual",
              label: t("verificationAccountTypeIndividual"),
            },
          ]}
          required
          error={errors.accountType}
        />
        <p className="mt-1 text-xs text-gray-500">
          {t("verificationAccountTypeHint")}
        </p>
      </div>

      {/* Company Name */}
      <Input
        label={t("verificationCompanyName")}
        value={data.companyName}
        onChange={handleChange("companyName")}
        placeholder={t("verificationCompanyNamePlaceholder")}
        required
        helperText={t("verificationCompanyNameHint")}
        error={errors.companyName}
      />

      {/* License Number */}
      <Input
        label={t("verificationLicenseNumber")}
        value={data.licenseNumber}
        onChange={handleChange("licenseNumber")}
        placeholder="東京都知事 (1) 第12345号"
        required
        helperText={t("verificationLicenseNumberHint")}
        error={errors.licenseNumber}
      />

      {/* Corporate Email */}
      <Input
        label={t("verificationCorporateEmail")}
        variant="email"
        value={data.corporateEmail}
        onChange={handleChange("corporateEmail")}
        placeholder="info@yourcompany.co.jp"
        required
        helperText={t("verificationCorporateEmailHint")}
        error={errors.corporateEmail}
      />

      {/* Official Website */}
      <Input
        label={t("verificationOfficialWebsite")}
        value={data.officialWebsite}
        onChange={handleChange("officialWebsite")}
        placeholder="https://www.yourcompany.co.jp"
        helperText={t("verificationOfficialWebsiteHint")}
        error={errors.officialWebsite}
      />
    </div>
  );
}
